import { t } from 'i18next';
import { useMutation } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { useSelectionCountContext } from 'core/states';
import { PricingProfileType } from 'pricing/enums';
import { pricingProfileService } from 'pricing/services';
import { projectPricingProfileService } from 'projects/services';

interface IProjectPricingProfileActionsProps {
  notificationPosition?: NotificationPosition;
  projectId?: number;
}

export const useProjectPricingProfileActions = ({
  notificationPosition = NotificationPosition.FORM,
  projectId,
}: IProjectPricingProfileActionsProps = {}) => {
  const { selectionCount } = useSelectionCountContext();

  return {
    ...useLinkProjectPricingProfile(notificationPosition),
    ...useUnlinkProjectPricingProfile(selectionCount, notificationPosition),
    ...useCreateAndLinkProjectPricingProfile(projectId, notificationPosition),
  };
};

const useLinkProjectPricingProfile = (notificationPosition: NotificationPosition) => {
  const { errors } = useHandleErrors({
    success: { message: t('messageProjectPricingProfileAddSuccess') },
    loading: { message: t('messageProjectPricingProfileAddInProgress') },
    error: { title: t('errorTitleProjectPricingProfileAdd') },
    invalidateQueries: ['corporatePricingProfiles'],
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation(
    (data: { pricingProfile: number; project: number }) =>
      projectPricingProfileService.addProjectPricingProfile(data),
    {
      ...errors,
    }
  );

  return { linkProjectPricingProfile: mutateAsync };
};

const useUnlinkProjectPricingProfile = (
  selectionCount: number,
  notificationPosition: NotificationPosition
) => {
  const { errors } = useHandleErrors({
    success: {
      message: t('messageProjectPricingProfileRemoveSuccess', { count: selectionCount }),
    },
    loading: {
      message: t('messageProjectPricingProfileRemoveInProgress', { count: selectionCount }),
    },
    error: {
      title: t('errorTitleProjectPricingProfileRemove', { count: selectionCount }),
    },
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation(
    (data: { ids: number[]; project: number }) =>
      projectPricingProfileService.removeProjectPricingProfile(data),
    {
      ...errors,
    }
  );

  return { unlinkProjectPricingProfile: mutateAsync };
};

const useCreateAndLinkProjectPricingProfile = (
  projectId: number,
  notificationPosition: NotificationPosition = NotificationPosition.FORM
) => {
  const { errors } = useHandleErrors({
    success: { message: t('messageProjectPricingProfileCreateSuccess') },
    loading: { message: t('messageProjectPricingProfileCreateInProgress') },
    error: { title: t('errorTitleProjectPricingProfileCreate') },
    invalidateQueries: ['corporatePricingProfiles'],
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation(
    async (data: { agreementId?: string; name: string; shareAccess?: boolean }) => {
      // Step 1: Create the pricing profile
      const createResponse = await pricingProfileService.upsertPricingProfile({
        pricingProfile: {
          agreementId: data.agreementId,
          name: data.name,
          shareAccess: data.shareAccess ? 1 : 0,
          type: PricingProfileType.PROJECT,
        },
      });

      if (!createResponse.success) {
        return createResponse;
      }

      // Step 2: Link to project
      const linkResponse = await projectPricingProfileService.addProjectPricingProfile({
        pricingProfile: createResponse.pricingProfile.id,
        project: projectId,
      });

      return linkResponse;
    },
    { ...errors }
  );

  return { createAndLinkPricingProfile: mutateAsync };
};
